import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectsLayout from '../layouts/ProjectsLayout';
import ResearchProjectModal from '../components/ResearchProjectModal';
import ResearchProjectPickerModal from '../components/ResearchProjectPickerModal';
import ConversationIngestModal from '../components/ConversationIngestModal';
import ResearchToolOutputBlock from '../components/ResearchToolOutputBlock';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import {
  API_BASE_URL,
  getAuthHeaders,
  listResearchProjects,
  listResearchJobs,
  getResearchJob,
  getResearchSteps,
  getResearchSources,
  cancelResearchJob,
  runResearchProject,
  archiveResearchProject,
  unarchiveResearchProject,
  getResearchMeta,
  linkResearchToChat,
} from '../utils/api';
import './ResearchPage.css';

const MODE_LABELS = {
  general_report: 'General report',
  competitive_analysis: 'Competitive analysis',
  grant_research: 'Grant research',
  investor_research: 'Investor research',
  prediction_market_contest: 'Prediction market contest',
  customer_discovery: 'Customer discovery',
  document_synthesis: 'Document synthesis',
};

const ACTIVE_STATUSES = new Set(['queued', 'running']);
/** Sidebar view that lists recent jobs across all projects (incl. chat-started). */
const RECENT_JOBS_VIEW = '__recent__';

function statusClass(status) {
  return `research-status research-status--${String(status || 'unknown').replace(/_/g, '-')}`;
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
}

function shortUserId(userId) {
  const s = String(userId || '').trim();
  if (!s) return '';
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

function projectToInitial(project) {
  if (!project) return null;
  return {
    id: project.id,
    title: project.title,
    prompt: project.prompt,
    mode: project.mode,
    provider: project.provider,
    model: project.model,
    maxSteps: project.maxSteps,
    maxSources: project.maxSources,
    options: project.options,
  };
}

function jobToInitial(job) {
  if (!job) return null;
  return {
    // Do not set project id here — re-run is job-based and must not PATCH another user's project.
    id: null,
    title: job.title,
    prompt: job.prompt,
    mode: job.mode,
    provider: job.provider,
    model: job.model,
    maxSteps: job.maxSteps,
    maxSources: job.maxSources,
    options: job.options,
  };
}

function ResearchPage() {
  const navigate = useNavigate();
  const { activeSuffix } = useGroup();
  const { user } = useAuth();
  const isSuperAdmin = String(user?.accountType || '').toLowerCase() === 'superadmin';
  const [meta, setMeta] = useState({ modes: [], defaults: {}, isSuperAdmin: false });
  const [projects, setProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(RECENT_JOBS_VIEW);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [steps, setSteps] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectModal, setProjectModal] = useState(null);
  const [pickerMode, setPickerMode] = useState(null);
  const [ingestProjectId, setIngestProjectId] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [runningProject, setRunningProject] = useState(false);

  const isRecentView = selectedProjectId === RECENT_JOBS_VIEW;
  const showAllUsers = Boolean(isSuperAdmin || meta?.isSuperAdmin);

  const selectedProject = useMemo(
    () => (isRecentView ? null : projects.find((p) => p.id === selectedProjectId) || null),
    [projects, selectedProjectId, isRecentView]
  );

  const ownsSelectedProject = !selectedProject
    || !user?.id
    || selectedProject.userId === user.id;

  const activeProjects = useMemo(
    () => projects.filter((p) => !p.archivedAt),
    [projects]
  );

  const archivedProjects = useMemo(
    () => projects.filter((p) => p.archivedAt),
    [projects]
  );

  const loadProjects = useCallback(async () => {
    const list = await listResearchProjects({ includeArchived: showArchived });
    setProjects(list);
    setSelectedProjectId((prev) => {
      if (prev === RECENT_JOBS_VIEW) return RECENT_JOBS_VIEW;
      if (prev && list.some((p) => p.id === prev)) return prev;
      // Prefer recent-jobs view so chat-started runs are visible even with no saved projects
      return RECENT_JOBS_VIEW;
    });
  }, [showArchived]);

  const loadJobs = useCallback(async (projectId) => {
    if (!projectId) {
      setJobs([]);
      return;
    }
    const list = projectId === RECENT_JOBS_VIEW
      ? await listResearchJobs({ limit: 50 })
      : await listResearchJobs({ projectId, limit: 50 });
    setJobs(list);
    setSelectedJobId((prev) => {
      if (prev && list.some((j) => j.id === prev)) return prev;
      return list[0]?.id || null;
    });
  }, []);

  const loadDetail = useCallback(async (jobId) => {
    if (!jobId) {
      setJobDetail(null);
      setSteps([]);
      setSources([]);
      return;
    }
    const [job, stepRows, sourceRows] = await Promise.all([
      getResearchJob(jobId),
      getResearchSteps(jobId),
      getResearchSources(jobId),
    ]);
    setJobDetail(job);
    setSteps(stepRows);
    setSources(sourceRows);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const m = await getResearchMeta();
        if (!cancelled) setMeta(m);
        await loadProjects();
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load research');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadProjects]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadJobs(selectedProjectId);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load runs');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProjectId, loadJobs]);

  const jobDetailStatus = jobDetail?.status;

  useEffect(() => {
    if (!selectedJobId) {
      setJobDetail(null);
      setSteps([]);
      setSources([]);
      return undefined;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        await loadDetail(selectedJobId);
        if (selectedProjectId) await loadJobs(selectedProjectId);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to refresh job');
      }
    };
    refresh();
    const interval = setInterval(() => {
      if (jobDetailStatus && ACTIVE_STATUSES.has(jobDetailStatus)) {
        refresh();
      }
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedJobId, jobDetailStatus, loadDetail, loadJobs, selectedProjectId]);

  const hasExportableContent = useMemo(() => {
    if (!jobDetail) return false;
    return !!(jobDetail.finalReport || jobDetail.structuredOutput);
  }, [jobDetail]);

  const openCreateModal = () => {
    setProjectModal({ mode: 'create', initial: {} });
  };

  const openEditModal = (project) => {
    setProjectModal({ mode: 'edit', initial: projectToInitial(project) });
  };

  const openRerunModal = (source) => {
    const job = source?.id ? source : null;
    const initial = jobToInitial(job) || {};
    setProjectModal({
      mode: 'rerun',
      initial,
      sourceJobId: job?.id || null,
    });
  };

  const handleProjectSaved = async ({ project, job, ran }) => {
    await loadProjects();
    if (project?.id) {
      setSelectedProjectId(project.id);
      await loadJobs(project.id);
    } else if (isRecentView || !selectedProjectId) {
      setSelectedProjectId(RECENT_JOBS_VIEW);
      await loadJobs(RECENT_JOBS_VIEW);
    } else if (selectedProjectId) {
      await loadJobs(selectedProjectId);
    }
    if (ran && job?.id) {
      setSelectedJobId(job.id);
    }
  };

  const handleRunProject = async () => {
    if (!selectedProject || selectedProject.archivedAt) return;
    setError('');
    setRunningProject(true);
    try {
      const job = await runResearchProject(selectedProject.id, { orgId: activeSuffix });
      await loadJobs(selectedProject.id);
      setSelectedJobId(job.id);
    } catch (err) {
      setError(err?.message || 'Failed to run research');
    } finally {
      setRunningProject(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!selectedProject) return;
    setError('');
    try {
      if (selectedProject.archivedAt) {
        await unarchiveResearchProject(selectedProject.id);
      } else {
        await archiveResearchProject(selectedProject.id);
      }
      await loadProjects();
    } catch (err) {
      setError(err?.message || 'Failed to update project');
    }
  };

  const handleLinkToChat = async ({ projectId }) => {
    const data = await linkResearchToChat(selectedJobId, projectId);
    const sessionId = data?.session_id;
    if (!sessionId) throw new Error('Chat session was not created');
    try {
      window.dispatchEvent(new CustomEvent('cascade:session-created', {
        detail: {
          session_id: sessionId,
          project_id: projectId,
          created_at: new Date().toISOString(),
          modified_at: new Date().toISOString(),
        },
      }));
    } catch (_) { /* no-op */ }
    setPickerMode(null);
    navigate(`/p/${projectId}/c/${sessionId}`);
  };

  const handleIngestProjectPick = async ({ projectId }) => {
    setIngestProjectId(projectId);
    setPickerMode(null);
  };

  const handleCancel = async () => {
    if (!selectedJobId) return;
    try {
      await cancelResearchJob(selectedJobId);
      await loadDetail(selectedJobId);
      await loadJobs(selectedProjectId);
    } catch (err) {
      setError(err?.message || 'Failed to cancel job');
    }
  };

  const exportUrl = (format) => {
    if (!selectedJobId) return '#';
    return `${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(selectedJobId)}/export/${format}`;
  };

  const handleExport = async (format) => {
    if (!selectedJobId || !hasExportableContent) return;
    setExporting(format);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(exportUrl(format), { headers });
      if (!resp.ok) {
        const json = await resp.json().catch(() => ({}));
        throw new Error(json?.error || `Export failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('content-disposition') || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match?.[1] || `research-${selectedJobId}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    <ProjectsLayout title="Research">
      <div className="research-page">
        <header className="research-header">
          <div>
            <h1>Deep Research</h1>
            <p>Save research settings per project, edit parameters, and re-run when jobs fail.</p>
          </div>
        </header>

        {error ? <div className="research-error" role="alert">{error}</div> : null}

        <div className="research-grid">
          <section className="research-panel research-panel--sidebar">
            <div className="research-sidebar-header">
              <h2>Research projects</h2>
              <button type="button" className="research-btn-secondary research-btn-compact" onClick={openCreateModal}>
                <i className="fas fa-plus" aria-hidden="true" />
                New
              </button>
            </div>

            <label className="research-sidebar-label">
              Project
              <select
                value={selectedProjectId || RECENT_JOBS_VIEW}
                onChange={(e) => {
                  setSelectedProjectId(e.target.value || RECENT_JOBS_VIEW);
                  setSelectedJobId(null);
                }}
              >
                <option value={RECENT_JOBS_VIEW}>
                  {showAllUsers ? 'All recent runs (all users)' : 'All recent runs'}
                </option>
                {activeProjects.length ? (
                  <optgroup label="Active projects">
                    {activeProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {showAllUsers && p.userId && p.userId !== user?.id
                          ? `${p.title} · ${shortUserId(p.userId)}`
                          : p.title}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {showArchived && archivedProjects.length ? (
                  <optgroup label="Archived">
                    {archivedProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {showAllUsers && p.userId && p.userId !== user?.id
                          ? `${p.title} · ${shortUserId(p.userId)}`
                          : p.title}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </label>

            <label className="research-sidebar-check">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>

            {selectedProject && ownsSelectedProject ? (
              <div className="research-sidebar-actions">
                <button
                  type="button"
                  className="research-btn-secondary research-btn-compact"
                  onClick={() => openEditModal(selectedProject)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="research-btn-secondary research-btn-compact"
                  onClick={handleArchiveToggle}
                >
                  {selectedProject.archivedAt ? 'Unarchive' : 'Archive'}
                </button>
              </div>
            ) : null}

            <h3 className="research-subheading">Runs</h3>
            {loading && !jobs.length ? <p className="research-muted">Loading…</p> : null}
            {isRecentView ? (
              <p className="research-muted">
                {showAllUsers
                  ? 'Showing recent jobs from all users (superadmin).'
                  : 'Showing recent jobs from chat and saved projects.'}
              </p>
            ) : null}
            <ul className="research-job-list">
              {jobs.map((job) => (
                <li key={job.id}>
                  <button
                    type="button"
                    className={`research-job-item${selectedJobId === job.id ? ' active' : ''}`}
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <span className="research-job-title">
                      {job.title || 'Untitled research'}
                    </span>
                    <span className="research-job-id" title={job.id}>{job.id}</span>
                    {showAllUsers && job.userId ? (
                      <span className="research-job-meta" title={job.userId}>
                        Owner: {shortUserId(job.userId)}
                        {job.userId === user?.id ? ' (you)' : ''}
                      </span>
                    ) : null}
                    <span className="research-job-meta">{formatDate(job.createdAt)}</span>
                    <span className={statusClass(job.status)}>{job.status}</span>
                  </button>
                </li>
              ))}
            </ul>
            {!loading && jobs.length === 0 ? (
              <p className="research-muted">No runs yet.</p>
            ) : null}
          </section>

          <section className="research-panel research-panel--detail">
            {!isRecentView && !selectedProject ? (
              <div className="research-empty">
                <p className="research-muted">Create or select a research project to get started.</p>
                <button type="button" className="research-submit research-submit--inline" onClick={openCreateModal}>
                  New research project
                </button>
              </div>
            ) : (
              <>
                {selectedProject ? (
                <div className="research-project-card">
                  <div className="research-project-card-main">
                    <h2>{selectedProject.title}</h2>
                    <p className="research-muted">
                      {MODE_LABELS[selectedProject.mode] || selectedProject.mode}
                      {' · '}
                      {selectedProject.provider}/{selectedProject.model}
                      {selectedProject.archivedAt ? ' · Archived' : ''}
                    </p>
                    {showAllUsers && selectedProject.userId ? (
                      <p className="research-muted" title={selectedProject.userId}>
                        Owner: {selectedProject.userId}
                        {selectedProject.userId === user?.id ? ' (you)' : ''}
                      </p>
                    ) : null}
                    <p className="research-project-prompt">{selectedProject.prompt}</p>
                  </div>
                  {ownsSelectedProject ? (
                  <div className="research-project-card-actions">
                    <button
                      type="button"
                      className="research-btn-secondary"
                      onClick={() => openEditModal(selectedProject)}
                    >
                      Edit settings
                    </button>
                    <button
                      type="button"
                      className="research-btn-secondary research-btn-primary-action"
                      onClick={handleRunProject}
                      disabled={runningProject || !!selectedProject.archivedAt}
                    >
                      {runningProject ? 'Starting…' : 'Run'}
                    </button>
                    <button
                      type="button"
                      className="research-btn-secondary"
                      onClick={handleArchiveToggle}
                    >
                      {selectedProject.archivedAt ? 'Unarchive' : 'Archive'}
                    </button>
                  </div>
                  ) : (
                  <div className="research-project-card-actions">
                    <p className="research-muted">Read-only (another user’s project)</p>
                  </div>
                  )}
                </div>
                ) : (
                <div className="research-project-card">
                  <div className="research-project-card-main">
                    <h2>{showAllUsers ? 'All recent runs (all users)' : 'All recent runs'}</h2>
                    <p className="research-muted">
                      {showAllUsers
                        ? 'Superadmin view: jobs started from chat and saved projects across all users.'
                        : 'Includes jobs started from chat and from saved research projects.'}
                    </p>
                  </div>
                  <div className="research-project-card-actions">
                    <button type="button" className="research-btn-secondary" onClick={openCreateModal}>
                      New research project
                    </button>
                  </div>
                </div>
                )}

                {selectedProject ? (
                <div className="research-runs-section">
                  <h3>Run history</h3>
                  {jobs.length === 0 ? (
                    <p className="research-muted">No runs yet. Edit settings and run this project.</p>
                  ) : (
                    <ul className="research-runs-table">
                      {jobs.map((job) => (
                        <li key={job.id} className={selectedJobId === job.id ? 'active' : ''}>
                          <button type="button" className="research-run-row" onClick={() => setSelectedJobId(job.id)}>
                            <span className="research-job-id" title={job.id}>{job.id}</span>
                            <span>{formatDate(job.createdAt)}</span>
                            <span className={statusClass(job.status)}>{job.status}</span>
                          </button>
                          {['failed', 'cancelled', 'needs_review'].includes(job.status) ? (
                            <button
                              type="button"
                              className="research-btn-secondary research-btn-compact"
                              onClick={() => openRerunModal(job)}
                            >
                              Edit &amp; re-run
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                ) : null}

                {!jobDetail ? (
                  <p className="research-muted">Select a run to view progress and results.</p>
                ) : (
                  <>
                    <div className="research-detail-header">
                      <div>
                        <h2>{jobDetail.title || 'Research run'}</h2>
                        <p className="research-job-id research-job-id--detail" title={jobDetail.id}>
                          Job ID: {jobDetail.id}
                        </p>
                        {showAllUsers && jobDetail.userId ? (
                          <p className="research-muted" title={jobDetail.userId}>
                            Owner: {jobDetail.userId}
                            {jobDetail.userId === user?.id ? ' (you)' : ''}
                          </p>
                        ) : null}
                        <p className="research-muted">
                          {formatDate(jobDetail.createdAt)}
                          {' · '}
                          {MODE_LABELS[jobDetail.mode] || jobDetail.mode}
                          {' · '}
                          {jobDetail.provider}/{jobDetail.model}
                        </p>
                      </div>
                      <span className={statusClass(jobDetail.status)}>{jobDetail.status}</span>
                    </div>

                    {jobDetail.prompt ? (
                      <div className="research-prompt-block">
                        <h3>Objective</h3>
                        <pre className="research-prompt-text">{jobDetail.prompt}</pre>
                      </div>
                    ) : null}

                    <div className="research-actions">
                      {ACTIVE_STATUSES.has(jobDetail.status) ? (
                        <button type="button" className="research-btn-secondary" onClick={handleCancel}>Cancel</button>
                      ) : null}
                      {['failed', 'cancelled', 'needs_review'].includes(jobDetail.status)
                        || (['completed'].includes(jobDetail.status) && !jobDetail.finalReport) ? (
                        <button
                          type="button"
                          className="research-btn-secondary research-btn-primary-action"
                          onClick={() => openRerunModal(jobDetail)}
                        >
                          Edit settings &amp; re-run
                        </button>
                      ) : null}

                      <div className="research-actions-group">
                        <span className="research-actions-label">Use results</span>
                        <button
                          type="button"
                          className="research-btn-secondary research-btn-primary-action"
                          disabled={!hasExportableContent}
                          onClick={() => setPickerMode('chat')}
                        >
                          <i className="fas fa-comments" aria-hidden="true" />
                          Open in project chat
                        </button>
                        <button
                          type="button"
                          className="research-btn-secondary research-btn-primary-action"
                          disabled={!hasExportableContent}
                          onClick={() => setPickerMode('ingest')}
                        >
                          <i className="fas fa-database" aria-hidden="true" />
                          Ingest to Horizon
                        </button>
                      </div>

                      <div className="research-actions-group">
                        <span className="research-actions-label">Export</span>
                        <button
                          type="button"
                          className="research-btn-secondary"
                          disabled={!hasExportableContent || exporting === 'markdown'}
                          onClick={() => handleExport('markdown')}
                        >
                          <i className="fas fa-file-lines" aria-hidden="true" />
                          {exporting === 'markdown' ? 'Exporting…' : 'Export as Markdown'}
                        </button>
                        <button
                          type="button"
                          className="research-btn-secondary"
                          disabled={!hasExportableContent || exporting === 'json'}
                          onClick={() => handleExport('json')}
                        >
                          <i className="fas fa-file-code" aria-hidden="true" />
                          {exporting === 'json' ? 'Exporting…' : 'Export as JSON'}
                        </button>
                        {jobDetail.mode === 'prediction_market_contest' ? (
                          <button
                            type="button"
                            className="research-btn-secondary"
                            disabled={!hasExportableContent || exporting === 'csv'}
                            onClick={() => handleExport('csv')}
                          >
                            <i className="fas fa-table" aria-hidden="true" />
                            {exporting === 'csv' ? 'Exporting…' : 'Export as CSV'}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {jobDetail.error ? (
                      <div className="research-error" role="alert">
                        <div>{jobDetail.error}</div>
                        {(() => {
                          const errStep = [...(steps || [])]
                            .reverse()
                            .find((s) => String(s.actionType || '').toLowerCase() === 'error'
                              || s.rawPayload?.category === 'llm_headers_timeout');
                          const category = errStep?.rawPayload?.category;
                          const suggestions = errStep?.rawPayload?.suggestions;
                          const progress = errStep?.rawPayload?.progress;
                          if (category !== 'llm_headers_timeout' && !suggestions?.length) return null;
                          return (
                            <div className="research-error-hint">
                              {category === 'llm_headers_timeout' ? (
                                <p>
                                  OpenAI didn’t return headers in time (common with gpt-5 + native web_search).
                                  Try Anthropic, enable <strong>Use Zagger web search</strong> on re-run,
                                  or raise client headers timeout (server already uses an extended undici timeout).
                                  {!progress?.enteredAgentLoop
                                    ? ' This run never entered the agent loop — no tokens or tool calls were received.'
                                    : null}
                                </p>
                              ) : null}
                              {Array.isArray(suggestions) && suggestions.length ? (
                                <ul>
                                  {suggestions.map((s) => (
                                    <li key={s}>{s}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          );
                        })()}
                      </div>
                    ) : null}

                    <ResearchToolOutputBlock job={jobDetail} steps={steps} defaultOpen />

                    <div className="research-sources-panel">
                      <h3>Sources ({sources.length})</h3>
                      <ul className="research-sources">
                        {sources.map((source) => (
                          <li key={source.id}>
                            <div className="research-source-title">{source.title || source.url || 'Untitled source'}</div>
                            {source.url ? (
                              <a href={source.url} target="_blank" rel="noopener noreferrer">{source.url}</a>
                            ) : null}
                            {source.snippet ? <p>{source.snippet}</p> : null}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {jobDetail.finalReport ? (
                      <div className="research-report">
                        <h3>Report</h3>
                        <pre>{jobDetail.finalReport}</pre>
                      </div>
                    ) : ['completed', 'needs_review'].includes(jobDetail.status) ? (
                      <div className="research-report research-report--empty">
                        <h3>Report</h3>
                        <p className="research-muted">
                          This run finished without stored report text (the agent ended on tool calls only).
                          Re-run the project after updating the server — new jobs generate a fallback write-up when needed.
                        </p>
                      </div>
                    ) : null}

                    {jobDetail.structuredOutput ? (
                      <div className="research-structured">
                        <h3>Structured output</h3>
                        <pre>{JSON.stringify(jobDetail.structuredOutput, null, 2)}</pre>
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      <ResearchProjectModal
        open={!!projectModal}
        mode={projectModal?.mode || 'create'}
        initial={projectModal?.initial || {}}
        sourceJobId={projectModal?.sourceJobId || null}
        meta={meta}
        orgId={activeSuffix}
        onClose={() => setProjectModal(null)}
        onSaved={handleProjectSaved}
      />

      <ResearchProjectPickerModal
        open={pickerMode === 'chat'}
        title="Open in project chat"
        description="Creates a new chat session with the research report as the first message."
        confirmLabel="Open chat"
        onClose={() => setPickerMode(null)}
        onSelect={handleLinkToChat}
      />

      <ResearchProjectPickerModal
        open={pickerMode === 'ingest'}
        title="Ingest research to Horizon"
        description="Choose the project whose ingestion planes should receive this research."
        confirmLabel="Continue to ingest"
        onClose={() => setPickerMode(null)}
        onSelect={handleIngestProjectPick}
      />

      <ConversationIngestModal
        open={!!ingestProjectId && !!selectedJobId}
        researchJobId={selectedJobId}
        projectId={ingestProjectId}
        suffix={activeSuffix}
        title="Ingest research into data planes"
        onClose={() => setIngestProjectId(null)}
      />
    </ProjectsLayout>
  );
}

export default ResearchPage;
